import { demoCatalog, demoCatalogById } from "./data/demoCatalog";

export const SITE_URL = "https://charehyar.ir";

export type SeoMetadata = {
  pathname: string;
  title: string;
  description: string;
  robots: string;
  canonicalUrl?: string;
  analyticsPath?: string;
};

const INDEX_ROBOTS = "index,follow,max-image-preview:large";
const NOINDEX_ROBOTS = "noindex,nofollow,noarchive";

export const publicRoutes = [
  {
    pathname: "/",
    title: "چاره‌یار | طراحی و اجرای راهکارهای کسب‌وکار",
    description: "چاره‌یار مسائل مدیریتی، عملیاتی و توسعه‌ای کسب‌وکارها را تحلیل می‌کند و با ترکیبی از استراتژی، فرایند، داده، فناوری، هوش مصنوعی و توسعه بازار، راهکار متناسب را تا مرحله اجرا پیش می‌برد.",
  },
  {
    pathname: "/approach",
    title: "رویکرد چاره‌یار | از شناخت مسئله تا اجرای راهکار",
    description: "با رویکرد چاره‌یار آشنا شوید؛ از شناخت وضعیت و ریشه‌یابی تا طراحی، اجرای آزمایشی، استقرار و انتقال دانش.",
  },
  {
    pathname: "/solutions",
    title: "خدمات و راهکارهای کسب‌وکار | چاره‌یار",
    description: "توانمندی‌های چاره‌یار از استراتژی و بهبود عملیات تا داده، نرم‌افزار اختصاصی، هوش مصنوعی، اتوماسیون و توسعه بازار؛ متناسب با مسئله و فرایند واقعی سازمان.",
  },
  {
    pathname: "/about",
    title: "درباره چاره‌یار | حل مسئله و اجرای راهکار",
    description: "چاره‌یار با ترکیب نگاه استراتژیک، اجرایی و فناورانه، فاصله میان تصمیم و اجرا را برای کسب‌وکارها کمتر می‌کند.",
  },
  {
    pathname: "/case-studies",
    title: "مطالعات موردی چاره‌یار | نمونه پروژه‌ها و خروجی‌های واقعی",
    description: "مطالعات موردی بی‌نام و مستند چاره‌یار؛ از استراتژی هلدینگ و معدن تا تداوم کسب‌وکار، تحلیل صنعت و مطالعات فرصت سرمایه‌گذاری.",
  },
  {
    pathname: "/showcase",
    title: "نمونه‌های تعاملی راهکارها | چاره‌یار",
    description: "نمونه‌های تعاملی چاره‌یار برای تصمیم‌گیری مدیریتی، برنامه‌ریزی عملیات، بازار، فروش، خدمات مشتری و ابزار کار در محل.",
  },
  {
    pathname: "/privacy",
    title: "حریم خصوصی و محرمانگی اطلاعات | چاره‌یار",
    description: "نحوه دریافت، استفاده، نگهداری و حذف اطلاعات و فایل‌های ارسالی در وب‌سایت چاره‌یار.",
  },
  {
    pathname: "/contact",
    title: "تماس با چاره‌یار | مسئله کسب‌وکار خود را مطرح کنید",
    description: "مسئله یا نیاز کسب‌وکار خود را با چاره‌یار مطرح کنید تا در یک بررسی اولیه، شرایط و اقدام بعدی را مشخص کنیم.",
  },
] as const;

const publicRouteByPath = new Map<string, (typeof publicRoutes)[number]>(
  publicRoutes.map((route) => [route.pathname, route]),
);

export const prerenderRoutes = [
  ...publicRoutes.map((route) => route.pathname),
  "/__control",
  ...demoCatalog.map((demo) => `/showcase/${demo.id}`),
];

function normalizePathname(pathname: string) {
  if (pathname === "/") return pathname;
  return pathname.replace(/\/+$/, "") || "/";
}

export function resolveSeo(pathname: string): SeoMetadata {
  const normalizedPathname = normalizePathname(pathname);
  if (normalizedPathname === "/diagnose" || normalizedPathname.startsWith("/diagnose/")) {
    return {
      pathname: normalizedPathname,
      title: "عارضه‌یاب سازمانی چاره‌یار",
      description: "فضای داخلی مدیریت پروژه‌های عارضه‌یابی سازمانی چاره‌یار.",
      robots: NOINDEX_ROBOTS,
    };
  }
  const publicRoute = publicRouteByPath.get(normalizedPathname);

  if (publicRoute) {
    return {
      ...publicRoute,
      robots: INDEX_ROBOTS,
      canonicalUrl: `${SITE_URL}${publicRoute.pathname === "/" ? "/" : publicRoute.pathname}`,
      analyticsPath: publicRoute.pathname,
    };
  }

  const demoId = normalizedPathname.match(/^\/showcase\/([^/]+)$/)?.[1] ?? "";
  const demo = demoCatalogById.get(demoId);
  if (demo) {
    return {
      pathname: normalizedPathname,
      title: `نمونه تعاملی ${demo.title} | چاره‌یار`,
      description: `این نمونه تعاملی برای نقش ${demo.audience} طراحی شده است و ${demo.supports} را نشان می‌دهد.`,
      robots: NOINDEX_ROBOTS,
      canonicalUrl: `${SITE_URL}${normalizedPathname}`,
      analyticsPath: normalizedPathname,
    };
  }

  if (normalizedPathname === "/__control") {
    return {
      pathname: normalizedPathname,
      title: "کنترل نمایش نمونه‌ها | چاره‌یار",
      description: "صفحه کنترل داخلی نمایش نمونه‌های چاره‌یار.",
      robots: NOINDEX_ROBOTS,
    };
  }

  return {
    pathname: normalizedPathname,
    title: "صفحه پیدا نشد | چاره‌یار",
    description: "آدرس درخواستی در وب‌سایت چاره‌یار پیدا نشد.",
    robots: NOINDEX_ROBOTS,
  };
}
